/**
 * Active-chart session storage.
 *
 * Uses localStorage so a just-cast chart survives a page refresh or
 * opening the URL in a new tab. Keys mirror the old sessionStorage
 * scheme (chart:${id}) so nothing else needs to change structurally.
 *
 * Falls back to sessionStorage if localStorage is unavailable (e.g.
 * Firefox with strict tracking protection on a fresh origin).
 */

import type { AstrologyInput } from "../api/types";
import { api } from "../api/client";
import { CELEBRITIES } from "./celebrities";
import { isDemoChart } from "./entitlements";


function storage(): Storage {
  try {
    localStorage.setItem("__test__", "1");
    localStorage.removeItem("__test__");
    return localStorage;
  } catch {
    return sessionStorage;
  }
}

/**
 * Bundled sample charts that resolve without any prior storage write.
 *
 * "vesper" is the sample exposed via the homepage's `Open a Sample` link and
 * by `/predictions/vesper`. Previously the route was session-bound and showed
 * "This reading link has expired" to every cold visitor; the curated entry
 * below ships in the bundle so the link always works.
 *
 * Picked Mahatma Gandhi (Oct 2 1869 · 07:28 Porbandar LMT, rectified within
 * the published 07:12–07:33 range; see backend RESEARCH-gandhi-lagna.md) — a
 * historical chart with strong, well-documented yogas so the sample reading
 * looks substantive rather than blank.
 */
const SAMPLE_CHARTS: Record<string, AstrologyInput> = {
  vesper: {
    year: 1869, month: 10, date: 2,
    hours: 7, minutes: 28, seconds: 0,
    latitude: 21.6417, longitude: 69.6293, timezone: 4.64,
    observation_point: "topocentric", ayanamsha: "lahiri",
  },
};

export function isSampleChart(id: string | null | undefined): boolean {
  return !!id && Object.prototype.hasOwnProperty.call(SAMPLE_CHARTS, id);
}

export function loadActiveChart(id: string): AstrologyInput | null {
  try {
    // Check localStorage first, then sessionStorage for legacy entries.
    const raw =
      localStorage.getItem(`chart:${id}`) ??
      sessionStorage.getItem(`chart:${id}`);
    if (raw) return JSON.parse(raw) as AstrologyInput;
  } catch {
    // Fall through to sample lookup so a broken local store doesn't kill the
    // sample-chart link.
  }
  
  const celeb = CELEBRITIES.find(c => c.id === id);
  if (celeb && celeb.input) {
    return celeb.input;
  }

  return SAMPLE_CHARTS[id] ?? null;
}

/** Scan storage for the most recently stored chart payload. */
export function getMostRecentChart(): { id: string; payload: AstrologyInput } | null {
  // Wrap the storage reference itself — in sandboxed iframes / strict privacy
  // modes, merely reading `localStorage` can throw before we reach the
  // per-iteration try/catch below. See M5 in the Watch redesign spec.
  let stores: Storage[];
  try {
    stores = [localStorage, sessionStorage];
  } catch {
    return null;
  }
  let best: { id: string; payload: AstrologyInput; ts: number } | null = null;
  for (const store of stores) {
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (!key || !key.startsWith("chart:")) continue;
      try {
        const raw = store.getItem(key);
        if (!raw) continue;
        const payload = JSON.parse(raw) as AstrologyInput;
        const id = key.replace("chart:", "");
        const ts = Number(store.getItem(`chart-ts:${id}`) || "0") || Date.now();
        if (!best || ts > best.ts) {
          best = { id, payload, ts };
        }
      } catch {
        continue;
      }
    }
  }
  return best ? { id: best.id, payload: best.payload } : null;
}

/** Persist a timestamp alongside a chart for recency sorting. */
export function storeActiveChart(id: string, payload: AstrologyInput): void {
  try {
    storage().setItem(`chart:${id}`, JSON.stringify(payload));
    storage().setItem(`chart-ts:${id}`, String(Date.now()));
  } catch {
    // Quota exceeded — silent no-op; chart still works for this tab.
  }
}

/**
 * Task 4A — Bind a chart as the session's active PERSONAL life.
 * DEMO CHARTS MUST NEVER call this — demos have their own pointer
 * (chart:active-demo-id) so the global "active personal chart" pointer
 * is never polluted by visiting a documented life. This is the fix for
 * the production defect where opening /reading/vesper caused Today to
 * treat the demo as the active personal chart.
 */
export function bindActiveChart(id: string, payload: AstrologyInput): void {
  if (isDemoChart(id)) {
    // Demo charts use bindDemoChart() instead. Falling through here
    // would corrupt the personal pointer; the explicit guard makes the
    // intent obvious to callers and to code review.
    bindDemoChart(id, payload);
    return;
  }
  storeActiveChart(id, payload);
  try {
    storage().setItem("chart:active-id", id);
  } catch {
    /* ignore */
  }
}

/**
 * Task 4A — Bind a DEMO chart for offline display. The payload is cached
 * under `chart:<id>` (same key shape as personal charts) but the
 * active-personal pointer is NEVER touched. A separate
 * `chart:active-demo-id` records which demo the user was last viewing
 * so cross-chapter demo navigation can resume it, but this is local
 * state — it does not surface as a "personal" chart anywhere.
 */
export function bindDemoChart(id: string, payload: AstrologyInput): void {
  storeActiveChart(id, payload);
  try {
    storage().setItem("chart:active-demo-id", id);
  } catch {
    /* ignore */
  }
}

/** Clear the active-id pointer so the homepage falls back to first-time CTAs.
 * Task 4A — wipes every per-chart key (chart, chart-ts, chart-meta,
 * chart-server-id) for the cleared id so `getMostRecentChart()` does
 * not immediately promote a stale cached chart as the "most recent"
 * and leak it back into the personal pointer via the recency fallback.
 * The user explicitly cleared — honor that. */
export function clearActiveChartId(): void {
  let clearedId: string | null = null;
  try {
    clearedId =
      localStorage.getItem("chart:active-id") ??
      sessionStorage.getItem("chart:active-id");
    storage().removeItem("chart:active-id");
  } catch {
    /* ignore */
  }
  if (clearedId) clearChart(clearedId);
}

/**
 * Last explicitly bound PERSONAL chart id. NEVER returns a demo chart
 * id — even if a legacy Task 4 build wrote one into the active pointer,
 * this getter quarantines it. Pages that need the personal chart for
 * "Your day" / "Continue your reading" / etc. must use this getter
 * (NOT getActiveDemoChartId).
 */
export function getActiveChartId(): string | null {
  try {
    const bound =
      localStorage.getItem("chart:active-id") ??
      sessionStorage.getItem("chart:active-id");
    if (bound) {
      // Defense in depth: never return a demo from the personal getter.
      if (!isDemoChart(bound)) return bound;
      // Demo leaked into personal pointer — quarantine.
      try {
        localStorage.setItem("chart:active-demo-id", bound);
        localStorage.removeItem("chart:active-id");
      } catch { /* ignore */ }
    }
  } catch {
    /* fall through */
  }
  // Fallback: most-recent chart. Filter out demos so visiting /reading/vesper
  // does NOT cause the personal getter to return "vesper" later.
  const recent = getMostRecentChart();
  if (recent && !isDemoChart(recent.id)) return recent.id;
  return null;
}

/**
 * Task 4A — The demo chart id the user was last viewing, if any.
 * Pages that want to "continue browsing the demo" can use this; pages
 * that want "continue YOUR reading" must NOT consult it.
 */
export function getActiveDemoChartId(): string | null {
  try {
    const v = localStorage.getItem("chart:active-demo-id");
    if (v && isDemoChart(v)) return v;
  } catch { /* ignore */ }
  return null;
}

/**
 * Task 4 — safe corruption recovery. Deletes every per-chart key
 * (chart, chart-ts, chart-meta, chart-server-id) but leaves
 * `chart:active-id` for the caller to clear explicitly. Used by the
 * Today page when getActiveChartId() resolves to a chart that no
 * longer parses or has been wiped by the user.
 */
export function clearChart(id: string): void {
  if (!id) return;
  const store = storage();
  for (const prefix of ["chart:", "chart-ts:", "chart-meta:", "chart-server-id:"]) {
    try {
      store.removeItem(`${prefix}${id}`);
    } catch {
      /* ignore */
    }
  }
}

/**
 * Task 4A — clear the active-demo pointer. Called when the user
 * explicitly switches away from demo browsing (e.g. visits /cast).
 */
export function clearActiveDemoId(): void {
  try { storage().removeItem("chart:active-demo-id"); } catch { /* ignore */ }
}

// ============================================================================
// Server-side chart caching (v2)
// ============================================================================

/** Store the server-assigned chart_id alongside a local id. */
export function storeServerChartId(localId: string, serverChartId: string): void {
  try {
    storage().setItem(`chart-server-id:${localId}`, serverChartId);
  } catch {
    // Quota exceeded — silent no-op.
  }
}

/** Retrieve the server chart_id mapped to a local id. */
export function getServerChartId(localId: string): string | null {
  try {
    return storage().getItem(`chart-server-id:${localId}`);
  } catch {
    return null;
  }
}

/**
 * Resolve a chart id — first from local storage, then via server cache.
 * If the id itself looks like a server chart_id (`chart-…`), try the
 * server directly. On success the birth data is stored locally so the
 * next load is instant.
 */
export async function hydrateChartFromServer(id: string): Promise<AstrologyInput | null> {
  const local = loadActiveChart(id);
  if (local) return local;

  // If the id itself is a server chart_id, query directly.
  if (id.startsWith("chart-")) {
    try {
      const res = await api.getChartById(id);
      if (res.success && res.birth) {
        storeActiveChart(id, res.birth);
        return res.birth;
      }
    } catch {
      // Server miss or error — fall through.
    }
  }

  // Otherwise see if we have a server mapping for this local id.
  const serverId = getServerChartId(id);
  if (serverId) {
    try {
      const res = await api.getChartById(serverId);
      if (res.success && res.birth) {
        storeActiveChart(id, res.birth);
        return res.birth;
      }
    } catch {
      // Server miss or error — fall through.
    }
  }

  return null;
}

/**
 * Task 4A — Migration: a previous Task 4 build wrote the active-personal
 * pointer to a demo chart id (vesper, mahatma-gandhi, etc.) when the
 * user visited /reading/<demo>. Calling this once on app boot
 * quarantines any such pointer into the demo namespace and clears the
 * personal pointer. Idempotent.
 *
 * Runs on every page load (cheap; one localStorage read + write).
 */
const MIGRATION_FLAG = "ag:task4a-demo-pointer-quarantine-v1";

export function migrateTask4aActivePointer(): void {
  if (typeof window === "undefined") return;
  let store: Storage;
  try { store = storage(); } catch { return; }
  // Single flag skips re-work after the first successful migration.
  try {
    if (store.getItem(MIGRATION_FLAG) === "1") return;
  } catch { /* ignore */ }

  let dirty = false;
  let activeId: string | null = null;
  try {
    activeId = store.getItem("chart:active-id");
  } catch { /* ignore */ }
  if (activeId && isDemoChart(activeId)) {
    // Move the bad pointer to chart:active-demo-id; clear personal pointer.
    try {
      store.setItem("chart:active-demo-id", activeId);
    } catch { /* ignore */ }
    try {
      store.removeItem("chart:active-id");
    } catch { /* ignore */ }
    dirty = true;
  }

  if (dirty) {
    try { store.setItem(MIGRATION_FLAG, "1"); } catch { /* ignore */ }
  } else {
    // Mark complete even if nothing changed so we don't re-read on every boot.
    try { store.setItem(MIGRATION_FLAG, "1"); } catch { /* ignore */ }
  }
}
